import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { Layout } from './components/Layout';
import { ClientLayout } from './components/ClientLayout';
import { SessionExpiryWatcher } from './components/SessionExpiryWatcher';
import { AuditListPage } from './features/audit/AuditListPage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { AuditDetailPage } from './features/audit/AuditDetailPage';
import { LoginPage } from './features/auth/LoginPage';
import { MfaSetupPage } from './features/auth/MfaSetupPage';
import { ChangePasswordPage } from './features/auth/ChangePasswordPage';
// DPIA module is paused for a rebuild; both routes render the placeholder.
// The real pages (DpiaListPage, ThresholdAssessmentPage) are kept in the
// repo and can be reactivated by swapping the route elements below back.
import { DpiaUnderConstructionPage } from './features/dpia/DpiaUnderConstructionPage';
import { AdminShell }        from './features/admin/AdminShell';
import { AdminUsersPage }    from './features/admin/AdminUsersPage';
import { AdminTenantsPage }  from './features/admin/AdminTenantsPage';
import { AdminAuditLogPage } from './features/admin/AdminAuditLogPage';
import { ClientReportsListPage } from './features/client/ClientReportsListPage';
import { ClientReportViewerPage } from './features/client/ClientReportViewerPage';
import { OrganisationsListPage } from './features/organisations/OrganisationsListPage';
import { OrganisationDetailPage } from './features/organisations/OrganisationDetailPage';
import { useCurrentUser } from './lib/api/queries';

// Staff router — DPAS staff (and paying consultancy staff, in a future
// SaaS world) get the full audit / DPIA / admin experience plus optional
// access to the /client shell for QA preview of the client view.
const staffRouter = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      // Slice 18c — the dashboard lives at /dashboard and '/' stays on the
      // audit list. Moving the staff landing page is a separate, reversible
      // decision worth making after a fortnight of actually using it.
      { index: true,            element: <AuditListPage /> },
      { path: 'dashboard',      element: <DashboardPage /> },
      { path: 'audits',         element: <AuditListPage /> },
      { path: 'audits/:id',     element: <AuditDetailPage /> },
      { path: 'dpia',           element: <DpiaUnderConstructionPage /> },
      { path: 'dpia/threshold', element: <DpiaUnderConstructionPage /> },
      // Slice 14 — client organisation admin (staff only)
      { path: 'organisations',       element: <OrganisationsListPage /> },
      { path: 'organisations/:id',   element: <OrganisationDetailPage /> },
      // Admin surface — Layout shows the Admin nav link conditionally on
      // user.isPlatformAdmin. Server-side, /api/admin/* is gated by the
      // SuperAdmin policy so a non-admin hitting these routes gets 403s.
      {
        path: 'admin',
        element: <AdminShell />,
        children: [
          { index: true,        element: <AdminUsersPage /> },
          { path: 'users',      element: <AdminUsersPage /> },
          { path: 'tenants',    element: <AdminTenantsPage /> },
          { path: 'log',        element: <AdminAuditLogPage /> },
        ],
      },
    ],
  },
  {
    // Staff can preview the client shell to see what a client sees before
    // hitting Publish. Server-side, /api/client/* is gated by the
    // ClientUser policy (requires client_organisation_id claim) so staff
    // previewing gets an empty list — a "staff preview" mode is a v2
    // capability.
    path: '/client',
    element: <ClientLayout />,
    children: [
      { index: true,                    element: <Navigate to="reports" replace /> },
      { path: 'reports',                element: <ClientReportsListPage /> },
      { path: 'reports/:snapshotId',    element: <ClientReportViewerPage /> },
    ],
  },
]);

// Client router — non-staff logins only ever see /client/*. Everything
// else redirects there so a bookmarked staff URL from a previous session
// (or a shared link) doesn't leave the user on an empty page.
const clientRouter = createBrowserRouter([
  {
    path: '/client',
    element: <ClientLayout />,
    children: [
      { index: true,                    element: <Navigate to="reports" replace /> },
      { path: 'reports',                element: <ClientReportsListPage /> },
      { path: 'reports/:snapshotId',    element: <ClientReportViewerPage /> },
    ],
  },
  // Any other URL — /audits, /dpia, /admin, / itself — force back to the
  // client home.
  { path: '*', element: <Navigate to="/client/reports" replace /> },
]);

/**
 * Top-level auth gate. Five states, in order:
 *
 *   1. session check pending           → tiny placeholder
 *   2. no user                         → {@link LoginPage}
 *   3. user with mustChangePassword    → {@link ChangePasswordPage}
 *   4. user but no MFA                 → {@link MfaSetupPage} (mandatory enrollment)
 *   5. otherwise                       → main app via {@link RouterProvider}
 *
 * Order matters: password change comes BEFORE MFA setup so an
 * admin-issued temp password never becomes a long-lived credential.
 * The server enforces the same ordering — the {@code MfaEnrolled} /
 * {@code DpasStaff} / {@code ClientUser} policies all refuse callers
 * whose cookie still carries {@code must_change_password = "true"}, so
 * a tampered client that skipped this page still can't reach anything
 * sensitive.
 */
export function App() {
  const { data: user, isLoading } = useCurrentUser();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">
        Loading…
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  // Once the user is signed in (even mid-enrollment), the session expiry
  // watcher needs to tick. Mounting it here covers the change-password
  // page, the MFA setup page, and the main app — anywhere a user holds
  // an authenticated cookie.
  //
  // Slice 13: pick the router based on the caller's staff/client flag.
  // Staff get the full audit + admin surface plus the /client preview
  // shell; client users get only /client/*.
  const router = user.isDpasStaff ? staffRouter : clientRouter;
  return (
    <>
      <SessionExpiryWatcher />
      {user.mustChangePassword
        ? <ChangePasswordPage />
        : !user.mfaEnabled
          ? <MfaSetupPage />
          : <RouterProvider router={router} />}
    </>
  );
}
